<template>
  <div class="dialog-size-md apply-template-dialog">
    <div class="modal-body">
      <div>
        <div v-if="currentMode === 'load'" class="load-template">
          <div class="template-header">
            <h1>
              {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.TEASER.HEADING') }}
            </h1>
            <p>
              {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.TEASER.TEXT1') }}
            </p>
          </div>
          <button
            v-tooltip="{
              content: enableTemplateSave
                ? null
                : $t(
                    'DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.SAVE.UNAVAILABLE',
                  ),
            }"
            class="btn btn-primary icon-btn save-template-button"
            :class="{ disabled: !enableTemplateSave }"
            @click="setCurrentMode('save')"
          >
            <Icon icon="savedisk" />
            {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.SAVE_CURRENT') }}
          </button>
          <h2>
            {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.LOAD_EXISTING') }}
          </h2>
          <TransitionGroup
            v-if="initialized"
            name="fade"
            class="template-list"
            tag="ul"
          >
            <li
              v-for="template in sortedTemplates"
              :key="template.phenotype.id"
              class="template-item"
            >
              <div class="name">
                <InlineEdit
                  v-model="template.phenotype.translation.name"
                  :label="$t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.NAME')"
                  :disabled="template.phenotype.global"
                  :max-length="35"
                  @on-edit-done="editName(template)"
                >
                  <div
                    class="input-counter"
                    :class="{
                      invalid: template.phenotype.translation.name.length > 35,
                    }"
                  >
                    {{ template.phenotype.translation.name.length }}/35
                  </div>
                </InlineEdit>
                <div v-if="template.phenotype.global" class="origin">
                  <div
                    v-if="template.phenotype.recommended"
                    class="recommended"
                  >
                    {{ $t('GENERAL.RECOMMENDED') }}
                  </div>
                  <MarketplaceLogo
                    v-if="
                      !template.phenotype.labels ||
                      (template.phenotype.labels &&
                        template.phenotype.labels.includes('MARKETPLACE') &&
                        hasMarketplace)
                    "
                    class="logo"
                  />
                  <SpreadshopLogo
                    v-if="
                      template.phenotype.labels &&
                      template.phenotype.labels.includes('SHOP') &&
                      hasShops
                    "
                    class="logo"
                  />
                  <img
                    v-if="
                      template.phenotype.labels &&
                      template.phenotype.labels.includes('MERCHANT_YOUTUBE') &&
                      hasYoutubeShops
                    "
                    class="logo"
                    src="/images/yt_logo.svg"
                    alt="Youtube Logo"
                  />
                </div>
                <div v-else class="origin">
                  {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.BY_ME') }}
                </div>
                <div v-if="isAdmin" class="template-id">
                  ID: {{ template.phenotype.id }}
                </div>
              </div>
              <div class="actions">
                <DoubleConfirmButton
                  v-if="!template.phenotype.global"
                  class="btn btn-default delete-btn btn-light btn-light-danger"
                  @on-confirm="deleteTemplate(template.phenotype.id)"
                >
                  <template #default="{ confirm }">
                    <Icon icon="bin"></Icon>
                    <span v-if="confirm">{{ $t('GENERAL.DELETE') }}</span>
                  </template>
                </DoubleConfirmButton>
                <ProgressButton
                  class="btn btn-primary btn-light icon-btn"
                  :on-click="() => applyTemplate(template)"
                  :label="$t('GENERAL.APPLY')"
                  icon="accept"
                />
              </div>
            </li>
          </TransitionGroup>
        </div>
        <div v-if="currentMode === 'save'" class="save-template">
          <div class="template-header">
            <h1>
              {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.SAVE.HEADING') }}
            </h1>
            <p>
              {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.SAVE.TEXT1') }}
            </p>
          </div>
          <div v-if="limitReached" class="limit-reached">
            {{ $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.LIMIT_REACHED') }}
          </div>
          <div v-else class="new">
            <LabelInput
              class="field"
              :class="{ invalid: submitted && hasError }"
            >
              <label>{{
                $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.NAME')
              }}</label>
              <input v-model="name" type="text" />
              <div class="field-info">
                <span v-if="!name" class="error-info">{{
                  $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.NAME_REQUIRED')
                }}</span>
                <div class="char-info" :class="{ invalid: name.length > 35 }">
                  {{ name.length }}/35
                </div>
              </div>
            </LabelInput>
            <ProgressButton
              class="btn btn-primary icon-btn"
              :on-click="createNewTemplate"
              :label="$t('GENERAL.SAVE')"
              icon="accept"
            />
          </div>
          <div v-if="initialized && userTemplates.length" class="replace">
            <h2>
              {{
                $t(
                  'DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.OVERWRITE_EXISTING.HEADING',
                )
              }}
            </h2>
            <p>
              {{
                $t(
                  'DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.OVERWRITE_EXISTING.SUBHEADING',
                )
              }}
            </p>
            <div>
              <TransitionGroup name="fade" tag="ul" class="template-list">
                <li
                  v-for="template in userTemplates"
                  :key="template.phenotype.id"
                  class="template-item"
                >
                  <div class="name">
                    <InlineEdit
                      v-model="template.phenotype.translation.name"
                      :label="
                        $t('DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.NAME')
                      "
                      @on-edit-done="editName(template)"
                    >
                      <div class="info">
                        <div
                          class="input-counter"
                          :class="{
                            invalid:
                              template.phenotype.translation.name.length > 35,
                          }"
                        >
                          {{ template.phenotype.translation.name.length }}/35
                        </div>
                      </div>
                    </InlineEdit>
                  </div>
                  <DoubleConfirmButton
                    class="btn btn-default delete-btn btn-light btn-light-danger"
                    @on-confirm="deleteTemplate(template.phenotype.id)"
                  >
                    <template #default="{ confirm }">
                      <Icon icon="bin"></Icon>
                      <span v-if="confirm">{{ $t('GENERAL.DELETE') }}</span>
                    </template>
                  </DoubleConfirmButton>
                  <ProgressButton
                    class="btn btn-primary btn-light icon-btn"
                    :on-click="() => saveTemplateAs(template.phenotype.id)"
                    :label="
                      $t(
                        'DESIGNS.CONFIGURATION.PRODUCT_TEMPLATES.SAVE.REPLACE_CTA',
                      )
                    "
                    icon="replace"
                  />
                </li>
              </TransitionGroup>
            </div>
          </div>
        </div>

        <div v-if="!initialized && !loadingError" class="loader">
          <img src="/images/loader.svg" alt="loader" class="sprd-loader" />
        </div>
        <div v-if="!initialized && loadingError" class="error">
          <Icon icon="broken-file" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import ideaTemplateService from 'commons/api/ideaTemplateService/ideaTemplateService';
import InlineEdit from 'commons/inlineEdit/InlineEdit.vue';
import DoubleConfirmButton from 'commons/doubleConfirmButton/DoubleConfirmButton.vue';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import analytics from 'commons/tracking/analytics';
import LabelInput from 'commons/labelInput/LabelInput.vue';
import productIdeaService from 'commons/api/productIdeaService/productIdeaService';
import MarketplaceLogo from 'commons/logos/MarketplaceLogo.vue';
import SpreadshopLogo from 'commons/logos/SpreadshopLogo.vue';
import assortmentHelper from 'commons/assortmentHelper/assortmentHelper';

export default {
  name: 'TemplateDialog',
  components: {
    InlineEdit,
    DoubleConfirmButton,
    ProgressButton,
    LabelInput,
    MarketplaceLogo,
    SpreadshopLogo,
  },
  props: ['modalInstance', 'data'],
  data() {
    return {
      currentMode: 'load',
      templates: null,
      initialized: false,
      loadingError: false,
      name: '',
      limitReached: false,
      submitted: false,
      templateLimit: 20,
    };
  },
  computed: {
    ...mapGetters({
      hasShops: 'user/hasShops',
      hasMarketplace: 'user/hasMarketplace',
      hasYoutubeShops: 'user/hasYoutubeShops',
      relevantTemplateLabels: 'idea/relevantTemplateLabels',
      isAdmin: 'user/isAdmin',
    }),
    enableTemplateSave() {
      return !assortmentHelper
        .getSelectedSellables(this.data.idea.assortment)
        .some(
          (product) =>
            product.included &&
            product.hints &&
            product.hints.includes('ONLY_FOR_POSTERS'),
        );
    },
    ...mapState({
      userLocale: (s) => s.user.locale,
    }),
    sortedTemplates() {
      return [...this.globalTemplates, ...this.userTemplates];
    },
    userTemplates() {
      return this.templates.list.filter(
        (template) => !template.phenotype.global,
      );
    },
    globalTemplates() {
      // partner should only see relevant global templates, recommended first
      return this.templates.list
        .filter((template) => template.phenotype.global)
        .filter(
          (template) =>
            !template.phenotype.labels ||
            (this.hasShops && template.phenotype.labels.includes('SHOP')) ||
            (this.hasYoutubeShops &&
              template.phenotype.labels.includes('MERCHANT_YOUTUBE')) ||
            (this.hasMarketplace &&
              template.phenotype.labels.includes('MARKETPLACE')),
        )
        .sort((a) => (a.phenotype.recommended ? -1 : 1));
    },
    hasError() {
      return (
        (this.templates && this.userTemplates.length >= this.templateLimit) ||
        !this.name ||
        this.name.length > 35
      );
    },
  },
  async created() {
    try {
      await this.loadTemplates();
      this.initialized = true;
    } catch (_) {
      this.loadingError = true;
    }
  },
  methods: {
    async loadTemplates() {
      this.templates = await ideaTemplateService.getTemplatesForIdea(
        this.data.idea.id,
        this.relevantTemplateLabels,
      );
    },
    applyTemplate(template) {
      analytics.logEvent('idea-template-apply', {
        global: template.phenotype.global,
      });

      return ideaTemplateService
        .applyTemplateForIdeas({
          templateId: template.phenotype.id,
          ideaIds: [this.data.idea.id],
        })
        .then(() => {
          return this.modalInstance.close({ action: this.currentMode });
        });
    },
    async deleteTemplate(templateId) {
      await ideaTemplateService.deleteTemplate(templateId);
      await this.loadTemplates();
    },
    async editName(template) {
      if (template.phenotype.translation.name.length > 35) {
        template.phenotype.translation.name =
          template.phenotype.translation.name.slice(0, 35);
      }

      await ideaTemplateService.updateTemplate({
        translation: template.phenotype.translation,
        phenotypeId: template.phenotype.id,
      });
      await this.loadTemplates();
    },
    async saveTemplateAs(templateId) {
      await productIdeaService.updateAssortment(this.data.idea);
      await ideaTemplateService.updateTemplate({
        ideaId: this.data.idea.id,
        phenotypeId: templateId,
      });
      setTimeout(() => {
        this.modalInstance.close({ action: this.currentMode });
      }, 1500);

      return Promise.resolve();
    },
    async createNewTemplate() {
      this.submitted = true;

      if (this.hasError) {
        return Promise.reject();
      }
      await productIdeaService.updateAssortment(this.data.idea);
      await ideaTemplateService.createTemplate({
        translation: {
          name: this.name,
          locale: this.userLocale,
        },
        ideaId: this.data.idea.id,
      });
      setTimeout(() => {
        this.modalInstance.close({ action: this.currentMode });
      }, 1500);

      return Promise.resolve();
    },
    setCurrentMode(mode) {
      if (mode === 'save' && !this.enableTemplateSave) {
        return;
      }

      this.currentMode = mode;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/animations';
@import 'src/scss/styleguide/colors';
@import 'src/scss/constants';
@import 'src/scss/styleguide/type';
@import 'src/scss/styleguide/buttons';
@import 'src/scss/utils';

.apply-template-dialog {
  max-width: 620px;
  width: 70vw;
}

.template-header {
  display: flex;
  flex-direction: column;

  h1 {
    margin: 0 0 12px 0;
  }

  p {
    margin: 0 0 16px 0;
    color: $grey60;
  }
}

.save-template-button {
  height: 40px;
}

.template-list {
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;

  .btn {
    border: none;
  }
}

.template-item {
  display: flex;
  padding: 16px;
  background-color: $grey5;
  border-radius: $border-radius-medium;

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  .name {
    flex-grow: 1;
    width: 60%;
    min-width: 30%;

    :deep(.inline-edit > p) {
      @extend .text-truncate;
    }
  }

  .origin {
    display: flex;
    align-items: center;
    margin-top: 5px;
    @extend .text-sm;

    .logo {
      height: 16px;

      &:not(:first-child) {
        margin-left: 12px;
      }
    }

    .recommended {
      padding: 5px 12px;
      background-color: $pa-color-main;
      color: $white;
      border-radius: 24px;
    }
  }

  .template-id {
    margin-top: 5px;
    @extend .text-sm;
  }

  .actions {
    display: flex;
    flex-shrink: 0;
  }

  button {
    height: 40px;
  }

  .delete-btn {
    margin-right: 10px;

    & > .icon {
      width: 24px;
      height: 24px;
    }

    &.confirm {
      @extend .btn-danger;
    }
  }

  :deep(.inline-edit) {
    margin-right: 16px;
    height: auto;

    p {
      @extend .heading4;
      font-weight: normal;
      margin: 0;
    }
  }
}

.save-template .template-item {
  :deep(.inline-edit) {
    height: 100%;
  }
}

.new {
  display: flex;
  position: relative;

  .field {
    flex-grow: 1;
  }

  .btn {
    white-space: nowrap;
    margin-left: 16px;
    height: 40px;
  }

  .char-info.invalid {
    color: $pa-color-red;
  }
}

.replace {
  h2 {
    margin-bottom: 8px;
  }

  p {
    margin: 0;
    color: $grey60;
  }
}

h2 {
  margin: 24px 0 16px 0;
}

.limit-reached {
  background-color: $pa-color-main;
  color: white;
  padding: 18px 48px 14px 48px;
  margin: 0 -20px;
  margin-bottom: 16px;
}

.error {
  display: flex;
  justify-content: center;
  padding: 28px 0;

  .icon {
    height: 48px;
    width: 48px;
  }
}

.input-counter {
  text-align: right;
  color: $sprd-color-grey-60;

  &.invalid {
    color: $pa-color-red;
  }
}
</style>
